import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../../components/loader/Loader";
import Portal from "../../../../../components/portal/Portal";
import TextWrapper from "../../../../../components/textWrapper/TextWrapper";
import { showConfirmationModal } from "../../../../../features/confirmationModal/ConfirmationModalSlice";
import { fetchAdminsByTournamentId } from "../../../../../features/controlledTournaments/controlledTournamentsSlice";
import { getLocalizedTime } from "../../../../../utils/timeZone";
import styles from "./oneTournamentAccess.module.css";

const OneTournamentAccess = ({
  tournamentId,
  status,
  name,
  serialNumber,
  createdBy,
  startDate,
  prize,
}) => {
  const { timeZone } = useSelector((state) => state.timeZone);
  const localizedStartDate = getLocalizedTime(startDate, timeZone);
  const [isOpen, setIsOpen] = useState(false);
  const [isChildrenOpen, setIsChildrenOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    left: 0,
    top: 0,
    maxHeight: 220,
  });
  const actionMenuRef = useRef(null);
  const menuRef = useRef(null);
  const closeMenuTimeoutRef = useRef(null);

  const dispatch = useDispatch();
  const { byAdmin, fetchStatus, addStatus, removeStatus } = useSelector(
    (state) => state.controlledTournaments
  );
  const { list: admins, fetchStatus: adminsFetchStatus } = useSelector(
    (state) => state.admins
  );

  const assignedAdmins = byAdmin[tournamentId] || [];
  const assignedStatus = fetchStatus[tournamentId];
  const adminsList = admins.filter(
    (admin) => !assignedAdmins.some((child) => child.adminId === admin._id)
  );

  // The assigned admins are only needed once this row's "+" menu or admin list
  // is opened, so they load then — not for every row on mount (the list can be
  // hundreds of tournaments, and that many requests at once trips the API's
  // per-account rate limit). A failure is retried by opening the row again,
  // never automatically: an effect that re-fetched on "failed" looped forever.
  const loadAssignedAdmins = useCallback(() => {
    if (assignedStatus === "loading" || assignedStatus === "succeeded") return;
    dispatch(fetchAdminsByTournamentId(tournamentId));
  }, [assignedStatus, dispatch, tournamentId]);

  const clearCloseMenuTimeout = useCallback(() => {
    if (closeMenuTimeoutRef.current) {
      clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!actionMenuRef.current) return;

    const rect = actionMenuRef.current.getBoundingClientRect();
    const spacing = 12;
    const menuWidth = 248;
    const preferredHeight = 220;
    const spaceBelow = window.innerHeight - rect.top - spacing;
    const spaceAbove = rect.bottom - spacing;
    const openUpward = spaceBelow < 180 && spaceAbove > spaceBelow;
    const availableSpace = Math.max(
      openUpward ? spaceAbove : spaceBelow,
      120
    );
    const maxHeight = Math.min(preferredHeight, availableSpace);

    setMenuPosition({
      left: Math.max(
        spacing,
        Math.min(rect.right + spacing, window.innerWidth - menuWidth - spacing)
      ),
      top: openUpward
        ? Math.max(spacing, rect.bottom - maxHeight)
        : Math.max(
            spacing,
            Math.min(rect.top, window.innerHeight - maxHeight - spacing)
          ),
      maxHeight,
    });
  }, []);

  const openMenu = useCallback(() => {
    clearCloseMenuTimeout();
    loadAssignedAdmins();

    if (
      adminsFetchStatus !== "loading" &&
      assignedStatus === "succeeded" &&
      adminsList.length === 0
    ) {
      setIsOpen(false);
      return;
    }

    updateMenuPosition();
    setIsOpen(true);
  }, [
    adminsFetchStatus,
    adminsList.length,
    assignedStatus,
    clearCloseMenuTimeout,
    loadAssignedAdmins,
    updateMenuPosition,
  ]);

  const scheduleCloseMenu = useCallback(() => {
    clearCloseMenuTimeout();
    closeMenuTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      closeMenuTimeoutRef.current = null;
    }, 120);
  }, [clearCloseMenuTimeout]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    const handlePointerDown = (event) => {
      const target = event.target;

      if (
        actionMenuRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    return () => {
      clearCloseMenuTimeout();
    };
  }, [clearCloseMenuTimeout]);

  const handleAddTournament = (adminId) => {
    setIsOpen(false);
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من اضافة الموظف للبطولة ؟",
        data: {
          adminId,
          tournamentIds: [tournamentId],
          tournamentId,
        },
        actionType: "addTournamentAdmins",
      })
    );
  };

  const handleRemoveTournament = (adminId) => {
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من ازالة الموظف للبطولة ؟",
        data: {
          adminId,
          tournamentIds: [tournamentId],
          tournamentId,
        },
        actionType: "removeTournamentAdmins",
      })
    );
  };

  const displayStatus =
    status === "upcoming"
      ? "قادمة"
      : status === "live"
        ? "مباشرة"
        : status === "ongoing"
          ? "جارية"
          : status === "completed"
            ? "منتهية"
            : status;

  // The menu lists admins NOT yet assigned, so it waits for the assigned list.
  const isMenuLoading =
    adminsFetchStatus === "loading" ||
    assignedStatus === undefined ||
    assignedStatus === "loading";
  // Opening the menu or the list again retries.
  const assignedFailed = assignedStatus === "failed";
  const loadFailedText = (
    <div className={styles.emptyState}>
      <TextWrapper textStyle={"textSmall"}>
        حصل خطأ اثناء جلب البيانات
      </TextWrapper>
    </div>
  );

  const isChildrenLoading =
    assignedStatus === "loading" ||
    addStatus[tournamentId] === "loading" ||
    removeStatus[tournamentId] === "loading";

  return (
    <div className={`${styles.mainContainer} ${isOpen ? styles.menuOpen : ""}`}>
      <div className={styles.container}>
        <div className={styles.infoContainer}>
          <div className={styles.statusWraper}>
            <TextWrapper textStyle="textMid">الحاله : {displayStatus}</TextWrapper>
          </div>
          <div className={styles.nameWrapper}>
            <TextWrapper textStyle="textMid">الاسم : {name}</TextWrapper>
          </div>
          <div className={styles.nameWrapper}>
            <TextWrapper textStyle="textMid">
              الرقم التسلسلي : {serialNumber}
            </TextWrapper>
          </div>
          <div className={styles.nameWrapper}>
            <TextWrapper textStyle="textMid">
              انشأت من قبل : {createdBy?.name}
            </TextWrapper>
          </div>
          <div className={styles.nameWrapper}>
            <TextWrapper textStyle="textMid">الجائزة : {prize}</TextWrapper>
          </div>
          <div className={styles.dateWrapper}>
            <TextWrapper textStyle="textXSmall">
              تبدأ من تاريخ :{" "}
              {localizedStartDate.toLocaleString().split(",")[0]}
            </TextWrapper>
          </div>
        </div>
        <div className={styles.optionsWrapper}>
          <div
            className={styles.actionMenu}
            ref={actionMenuRef}
            onMouseEnter={openMenu}
            onMouseLeave={scheduleCloseMenu}
          >
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => {
                if (isOpen) {
                  clearCloseMenuTimeout();
                  setIsOpen(false);
                } else {
                  openMenu();
                }
              }}
              aria-expanded={isOpen}
            >
              <span className={styles.plusIcon}>+</span>
            </button>
          </div>
          <button
            type="button"
            className={
              isChildrenOpen
                ? `${styles.iconButton} ${styles.opened}`
                : styles.iconButton
            }
            onClick={() => {
              if (!isChildrenOpen) loadAssignedAdmins();
              setIsChildrenOpen((prev) => !prev);
            }}
          >
            <span className={styles.chevronIcon}>▾</span>
          </button>
        </div>
      </div>
      {isOpen ? (
        <Portal>
          <div
            ref={menuRef}
            className={styles.adminsWrapper}
            style={{
              left: `${menuPosition.left}px`,
              top: `${menuPosition.top}px`,
              maxHeight: `${menuPosition.maxHeight}px`,
            }}
            onMouseEnter={clearCloseMenuTimeout}
            onMouseLeave={scheduleCloseMenu}
          >
            {assignedFailed ? (
              loadFailedText
            ) : isMenuLoading ? (
              <div className={styles.loaderContainer}>
                <Loader />
              </div>
            ) : (
              adminsList.map((admin) => {
                return (
                  <div
                    key={admin._id}
                    className={styles.oneAdminWrapper}
                    onClick={() => handleAddTournament(admin._id)}
                  >
                    <TextWrapper textStyle={"textMid"}>
                      اسم الموظف : {admin?.name}
                    </TextWrapper>
                    <TextWrapper textStyle={"textMid"}>
                      الوظيفه : {admin?.role?.role}
                    </TextWrapper>
                  </div>
                );
              })
            )
            }
            {!isMenuLoading && !assignedFailed && adminsList.length === 0 ? (
              <div className={styles.emptyState}>
                <TextWrapper textStyle={"textSmall"}>
                  لا يوجد موظفون متاحون
                </TextWrapper>
              </div>
            ) : null}
          </div>
        </Portal>
      ) : null}
      <div className={styles.childrenContainer}>
        {isChildrenOpen ? (
          isChildrenLoading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : assignedFailed ? (
            loadFailedText
          ) : (
            assignedAdmins.map((child, index) => {
              return (
                <div key={child.adminId} className={styles.child}>
                  <div className={styles.childContent}>
                    <div className={styles.childInfo}>
                      <TextWrapper textStyle={"textMid"}>
                        اسم الموظف : {child?.name}
                      </TextWrapper>
                      <TextWrapper textStyle={"textMid"}>
                        الوظيفه : {child?.role?.role}
                      </TextWrapper>
                    </div>
                    <button
                      type="button"
                      className={styles.removeAdminWrapper}
                      onClick={() => handleRemoveTournament(child.adminId)}
                    >
                      <TextWrapper textStyle={"textSmall"}>
                        ازالة الموظف
                      </TextWrapper>
                    </button>
                  </div>
                  <div className={styles.indexContainer}>
                    <TextWrapper textStyle={"textSmall"}>{index + 1}</TextWrapper>
                  </div>
                </div>
              );
            })
          )
        ) : null}
      </div>
    </div>
  );
};

export default OneTournamentAccess;
